import { useState, useEffect } from 'react';
import { useToast } from '../../../../providers/ToastProvider';
import { useAuth } from '../../../../auth/Auth';
import { SessionApi } from '../../../../apis/SessionApi';
import { GpevAuthorizeApi } from '../../../../apis/GpevAuthorizeApi';
import { nowPlusHoursUnix, parseChargeLink, parseGPEVChargeLink, uuid4 } from '../../../../utilities/generalUtils';

export function useChargeSession({
    userVehicle,
    guestChargers,
    canUseSharedCharger,
    smartOutlets,
    onChargeStarted,
    onError,
}) {
    const { showToast } = useToast();
    const { userId } = useAuth();

    const [openQR, setOpenQR] = useState(false);
    const [isRequestingCharge, setIsRequestingCharge] = useState(false);
    const [chargeRequestTimeout, setChargeRequestTimeout] = useState(null);
    const [pendingGPEVScan, setPendingGPEVScan] = useState(null);
    const [selectedOutlet, setSelectedOutlet] = useState('L1');
    const [isAuthorizing, setIsAuthorizing] = useState(false);

    const getChargePointByUuid = (deviceUuid) => {
        if (!deviceUuid) return null;
        const deviceId = String(deviceUuid).trim().toLowerCase();
        return guestChargers?.chargePoints?.find(
            (cp) => String(cp.uuid).toLowerCase() === deviceId
        ) || null;
    };

    const startChargeRequest = (payload) => {
        setIsRequestingCharge(true);

        const timeoutId = setTimeout(() => {
            setIsRequestingCharge(false);
            setChargeRequestTimeout(null);
            showToast({
                type: 'error',
                title: 'Request Failed',
                message: 'Charge request timed out. Please try again.',
            });
            onError && onError('timeout');
        }, 45000);

        setChargeRequestTimeout(timeoutId);

        SessionApi.start(payload).then((r) => {
            onChargeStarted && onChargeStarted(r);
        }).catch((error) => {
            if (chargeRequestTimeout) {
                clearTimeout(chargeRequestTimeout);
                setChargeRequestTimeout(null);
            }
            setIsRequestingCharge(false);
            showToast({
                type: 'error',
                title: 'Error',
                message: 'Unable to start your charging request',
            });
            onError && onError(error);
        });
    };

    const handleGPEVDetected = (qrText) => {
        let parsed;
        try {
            parsed = parseGPEVChargeLink(qrText);
        } catch (err) {
            showToast({
                type: 'error',
                title: 'Error',
                message: 'Unable to decode QR code. Try again.',
            });
            return;
        }

        const macLower = parsed.macAddress.toLowerCase();
        const matchedSO = smartOutlets?.find(
            so => String(so.macAddr || '').toLowerCase() === macLower
        );

        if (!matchedSO) {
            showToast({
                type: 'error',
                title: 'Error',
                message: 'This charger is not available at your property.',
            });
            return;
        }

        setOpenQR(false);
        setIsAuthorizing(true);

        GpevAuthorizeApi.authorize(parsed.macAddress).then(() => {
            setIsAuthorizing(false);
            setSelectedOutlet('L1');
            setPendingGPEVScan({ ...parsed, matchedSO });
        }).catch(() => {
            setIsAuthorizing(false);
            showToast({
                type: 'error',
                title: 'Access Denied',
                message: 'You are not authorized to use this charger.',
            });
        });
    };

    const handleOCPPDetected = (qrText) => {
        let deviceId, model;

        try {
            ({ deviceId, model } = parseChargeLink(qrText));
        } catch (err) {
            showToast({
                type: 'error',
                title: 'Error',
                message: 'Unable to decode QR code. Try again.',
            });
            return;
        }

        const canUseCharger = canUseSharedCharger(model);
        if (!canUseCharger) {
            showToast({
                type: 'error',
                title: 'Error',
                message: 'This charger model is unavailable',
            });
            return;
        }

        const guestCharger = getChargePointByUuid(deviceId);
        if (!guestCharger) {
            showToast({
                type: 'error',
                title: 'Error',
                message: 'Unable to find guest charger. Please try again',
            });
            return;
        }

        setOpenQR(false);

        startChargeRequest({
            chargingOutlet: '1',
            departureTime: nowPlusHoursUnix(8),
            milesRequested: 0,
            userVehicleUuid: userVehicle?.uuid,
            userUuid: userId,
            powerMode: 2,
            modifyFlag: 0,
            sessionUuid: uuid4(),
            serialNumber: guestCharger.serialNumber,
            deviceUuid: deviceId,
            chargerType: 'OCPP',
        });
    };

    const handleDetected = (qrText) => {
        const trimmed = qrText.trim();
        if (!trimmed.startsWith('http') && trimmed.includes(',')) {
            handleGPEVDetected(trimmed);
        } else {
            handleOCPPDetected(trimmed);
        }
    };

    const handleConfirmGPEV = () => {
        const payload = {
            chargingOutlet: selectedOutlet,
            departureTime: nowPlusHoursUnix(8),
            milesRequested: 0,
            userVehicleUuid: userVehicle?.uuid,
            userUuid: userId,
            modifyFlag: 0,
            powerMode: 2,
            sessionUuid: uuid4(),
            macAddress: pendingGPEVScan.macAddress,
            chargerType: 'GPEV',
        };
        setPendingGPEVScan(null);
        startChargeRequest(payload);
    };

    const handleStartCharge = () => setOpenQR(true);
    const handleCloseQR = () => setOpenQR(false);

    useEffect(() => {
        return () => {
            if (chargeRequestTimeout) {
                clearTimeout(chargeRequestTimeout);
            }
        };
    }, [chargeRequestTimeout]);

    useEffect(() => {
        if (!isRequestingCharge && chargeRequestTimeout) {
            clearTimeout(chargeRequestTimeout);
            setChargeRequestTimeout(null);
        }
    }, [isRequestingCharge, chargeRequestTimeout]);

    return {
        openQR,
        isRequestingCharge,
        isAuthorizing,
        pendingGPEVScan,
        selectedOutlet,
        setSelectedOutlet,
        setPendingGPEVScan,
        handleStartCharge,
        handleCloseQR,
        handleDetected,
        handleConfirmGPEV,
    };
}
